/* eslint-disable react-hooks/exhaustive-deps */
import './SettingsCounselor.css';
import './SettingsParent.css';
import './SettingsStudents.css';
import React from 'react';
import { get } from 'lodash';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { Button, Spinner } from 'react-bootstrap';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import MuiButton from '@mui/material/Button';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TextIconInput from 'components/input/TextIconInput';
import { INVITATION_STATUS, STRIPE_PRICE_NAME, USER_ROLE } from 'constants/common';
import {
  isInviteListRowConnected,
  isParentInviteActionPending,
  normalizeInviteBy,
} from 'utils/invitePartnerList';
import { catName, toTitleCase } from 'components/helper/helper';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useHistory } from 'react-router-dom';
import { routers } from 'router/router';
import partnerServices from 'services/partnerServices';
import basicInformationServices from 'services/basicInformationApi';
import {
  getCurrentBasicDetail,
  logout,
  setCurrentStudentId,
} from 'app/basicInformationSlice/basicInformationSlice';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';
import { store } from 'app/store';
import { useChatList } from 'components/layout/ChatListContext';

interface Props {}

function formatRelationshipLabel(raw: string | undefined) {
  if (!raw || !String(raw).trim()) return 'Parent / Guardian';
  return toTitleCase(String(raw).replace(/_/g, ' '));
}

function relationLabelOrParent(raw: string | undefined | null) {
  if (raw == null || !String(raw).trim()) return 'Parent';
  return formatRelationshipLabel(String(raw));
}

function getPlanDisplayName(plan: string | undefined) {
  if (!plan) return 'Starter (Free)';
  switch (plan) {
    case STRIPE_PRICE_NAME.GoldMonth:
    case STRIPE_PRICE_NAME.GoldYear:
      return 'Gold Plan';
    case STRIPE_PRICE_NAME.SilverPlusMonth:
    case STRIPE_PRICE_NAME.SilverPlusYear:
      return 'Silver Plus Plan';
    case STRIPE_PRICE_NAME.SilverMonth:
    case STRIPE_PRICE_NAME.SilverYear:
      return 'Silver Plan';
    case STRIPE_PRICE_NAME.GradMapStarter:
      return 'Starter (Free)';
    default:
      return plan;
  }
}

function planBadgeClass(plan: string | undefined): 'gold' | 'silver' | 'starter' {
  if (!plan || plan === STRIPE_PRICE_NAME.GradMapStarter) return 'starter';
  if (plan === STRIPE_PRICE_NAME.GoldMonth || plan === STRIPE_PRICE_NAME.GoldYear) return 'gold';
  return 'silver';
}

/** Same shape as ParentMainDashboard / ParentStudentProfilePage (api/stripe/is-enable-extention). */
function rawPlanFromStripeSubscriptionInfo(info: any): string | undefined {
  const raw = get(info, 'subcription_plan') || get(info, 'payload.subcription_plan');
  return raw ?? undefined;
}

const DEMO_STUDENT_EMAIL = 'student-demo@gradmap.com';

function isDemoStudentEmail(email: string | undefined | null): boolean {
  return (email ?? '').trim().toLowerCase() === DEMO_STUDENT_EMAIL;
}

const DEMO_ACCOUNT_ACTION_MESSAGE =
  'Đây chỉ là tài khoản demo — không thể thực hiện thao tác trên tài khoản này.';

const SettingsStudents = (props: Props) => {
  let token = getAccessToken();
  const dispatch: any = useAppDispatch();
  const history = useHistory();
  const { refreshChatList } = useChatList();
  const [waiting, setWaiting] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<'approve' | 'decline' | null>(null);
  const [currentRow, setCurrentRow] = React.useState<any>();
  const [partnerList, setPartnerList] = React.useState<any[]>([]);
  const [outgoingEmailInvites, setOutgoingEmailInvites] = React.useState<any[]>([]);
  const [inviteStudentNamesLocked, setInviteStudentNamesLocked] = React.useState(false);
  const [inviteStudentEmailChecking, setInviteStudentEmailChecking] = React.useState(false);
  const [cancelInviteOpen, setCancelInviteOpen] = React.useState(false);
  const [cancelInviteRow, setCancelInviteRow] = React.useState<any>(null);
  const [resendErrorOpen, setResendErrorOpen] = React.useState(false);
  const [resendErrorMessage, setResendErrorMessage] = React.useState('');
  const [copiedRowKey, setCopiedRowKey] = React.useState<string | null>(null);
  const [removeStudentOpen, setRemoveStudentOpen] = React.useState(false);
  const [removeStudentRow, setRemoveStudentRow] = React.useState<any>(null);
  /** Initial fetch of students / outgoing invites (parent Students tab) */
  const [studentSettingsLoading, setStudentSettingsLoading] = React.useState(true);
  /** Per-student plan from getStripeSubscriptionInfo — matches dashboard, not partner list alone. */
  const [subscriptionPlanByStudentId, setSubscriptionPlanByStudentId] = React.useState<
    Record<string, string | undefined>
  >({});

  const isInviteStudentEmailAlreadyListed = React.useCallback(
    (email: string | undefined) => {
      const e = (email ?? '').trim().toLowerCase();
      if (!e) return false;
      return partnerList.some((p: any) => (p.email ?? '').trim().toLowerCase() === e);
    },
    [partnerList]
  );

  let personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const role: string = personalStore?.user_role ?? sessionStorage.getItem('userRole') ?? '';
  const parentId = personalStore?.student_id ?? getUserId();

  const partnerRowKey = (row: any) => String(row?.id ?? `${row?.student_id}-${row?.email ?? ''}`);
  const outgoingInviteRowKey = (row: any) => String(row?.id ?? row?.invitee_email ?? 'invite');

  const toastDemoAccountBlocked = React.useCallback(() => {
    toast.error(DEMO_ACCOUNT_ACTION_MESSAGE);
  }, []);

  const inviteformik = useFormik({
    initialValues: {
      student_email: '',
      student_first_name: '',
      student_last_name: '',
    },
    validationSchema: Yup.object({
      student_email: Yup.string().email('Invalid email').required('Required'),
      student_first_name: Yup.string(),
      student_last_name: Yup.string(),
    }),
    onSubmit: async (values) => {
      if (isDemoStudentEmail(values.student_email?.trim())) {
        toastDemoAccountBlocked();
        return;
      }
      token = getAccessToken();
      if (token && parentId) {
        setWaiting(true);
        try {
          await dispatch(getCurrentBasicDetail(parentId)).unwrap();
          const emailTrim = values.student_email?.trim() ?? '';
          if (isInviteStudentEmailAlreadyListed(emailTrim)) {
            setWaiting(false);
            return;
          }
          const studentObj: any = await partnerServices.getStudentByEmail(emailTrim);

          if (studentObj && studentObj.user_role === USER_ROLE.student) {
            await partnerServices.parent_InviteStudent(parentId, emailTrim);
            inviteformik.setErrors({});
            inviteformik.resetForm();
            setInviteStudentNamesLocked(false);
            toast.success('Your student has been invited.');
            await getDetail(true);
            refreshChatList();
          } else {
            const fn = values.student_first_name?.trim() ?? '';
            const ln = values.student_last_name?.trim() ?? '';
            if (!fn || !ln) {
              inviteformik.setErrors({
                student_email: '',
                student_first_name: !fn ? 'Required' : '',
                student_last_name: !ln ? 'Required' : '',
              });
              setWaiting(false);
              return;
            }
            await partnerServices.parent_InviteStudent(parentId, emailTrim, fn, ln);
            inviteformik.setErrors({});
            inviteformik.resetForm();
            setInviteStudentNamesLocked(false);
            toast.success('Your student has been invited.');
            await getDetail(true);
            refreshChatList();
          }
        } catch (error: any) {
          let strError: string = error.message;
          if (error.response) {
            const serverData = error.response.data;
            strError = serverData.message || 'An expected error occurred';
          } else if (error.request) {
            strError = 'Network Error: Server did not respond.';
          } else {
            strError = 'An unexpected client error occurred.';
          }

          inviteformik.setErrors({
            student_email: `${strError}`,
            student_first_name: '',
            student_last_name: '',
          });
        }

        setWaiting(false);
      } else {
        handleLogout();
      }
    },
  });

  const handleInviteStudentEmailBlur = async () => {
    const raw = inviteformik.values.student_email?.trim() ?? '';
    inviteformik.setFieldTouched('student_email', true);
    if (!raw) {
      if (inviteStudentNamesLocked) {
        inviteformik.setFieldValue('student_first_name', '');
        inviteformik.setFieldValue('student_last_name', '');
      }
      setInviteStudentNamesLocked(false);
      return;
    }
    const basicEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicEmail.test(raw)) {
      setInviteStudentNamesLocked(false);
      return;
    }
    if (isInviteStudentEmailAlreadyListed(raw)) {
      if (inviteStudentNamesLocked) {
        inviteformik.setFieldValue('student_first_name', '');
        inviteformik.setFieldValue('student_last_name', '');
      }
      setInviteStudentNamesLocked(false);
      inviteformik.setFieldError('student_email', undefined);
      return;
    }
    try {
      setInviteStudentEmailChecking(true);
      const studentObj: any = await partnerServices.getStudentByEmail(raw);
      if (studentObj && studentObj.user_role === USER_ROLE.student) {
        inviteformik.setFieldValue('student_first_name', studentObj.first_name ?? '');
        inviteformik.setFieldValue('student_last_name', studentObj.last_name ?? '');
        setInviteStudentNamesLocked(true);
        inviteformik.setFieldError('student_email', undefined);
      } else {
        setInviteStudentNamesLocked(false);
        inviteformik.setFieldError('student_email', undefined);
      }
    } catch {
      setInviteStudentNamesLocked(false);
    } finally {
      setInviteStudentEmailChecking(false);
    }
  };

  const isParentInviteRowActive = (r: any) => {
    const s = r?.requested_status;
    return s === INVITATION_STATUS.approved || s === INVITATION_STATUS.pending;
  };

  const loadStripePlansForPartnerRows = React.useCallback(
    async (rows: any[]) => {
      const ids = Array.from(
        new Set(
          rows
            .map((r) => r?.student_id)
            .filter((id) => id != null && id !== '')
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
        )
      );
      if (ids.length === 0) {
        setSubscriptionPlanByStudentId({});
        return;
      }
      const entries = await Promise.all(
        ids.map(async (sid) => {
          try {
            // Direct API — avoid overwriting global stripeSubscriptionInfo with each child
            const info: any = await basicInformationServices.getEnableExtention(sid);
            const raw = rawPlanFromStripeSubscriptionInfo(info);
            return [String(sid), raw] as const;
          } catch {
            return [String(sid), undefined] as const;
          }
        })
      );
      setSubscriptionPlanByStudentId(Object.fromEntries(entries));
    },
    []
  );

  const loadParnerList = async () => {
    if (!parentId) return;
    try {
      const res = await partnerServices.parentGetInvitePartnerList(parentId);
      if (res && res.length > 0) {
        const arr = res.filter((r: any) => isParentInviteRowActive(r));
        const mockData = arr.map((item: any) => ({
          ...item,
          processState: 'No Process',
          chat: Math.floor(Math.random() * 5),
          notes: Math.floor(Math.random() * 6),
          todos: Math.floor(Math.random() * 4),
          lastActivity: 'No activity',
        }));
        setPartnerList(mockData);
        await loadStripePlansForPartnerRows(mockData);
      } else {
        setPartnerList([]);
        setSubscriptionPlanByStudentId({});
      }
    } catch {
      setPartnerList([]);
      setSubscriptionPlanByStudentId({});
    }
  };

  const loadOutgoingInvites = React.useCallback(async () => {
    if (!parentId) return;
    try {
      const data = await partnerServices.getMyOutgoingPartnerEmailInvites();
      const raw = data?.invites ?? (data as any)?.result?.invites ?? [];
      const list = Array.isArray(raw) ? raw : [];
      setOutgoingEmailInvites(list.filter((i: any) => i.consumed_at == null));
    } catch {
      setOutgoingEmailInvites([]);
    }
  }, [parentId]);

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const getDetail = async (silent = false) => {
    token = getAccessToken();
    if (!token || !parentId) {
      setStudentSettingsLoading(false);
      handleLogout();
      return;
    }
    if (!silent) {
      setStudentSettingsLoading(true);
    }
    try {
      const resolvedRole =
        store.getState().basicInformation.myBasicInformation?.user_role ??
        sessionStorage.getItem('userRole') ??
        role ??
        '';
      if (resolvedRole === USER_ROLE.parent) {
        await loadParnerList();
        await loadOutgoingInvites();
      }
    } catch (e) {
      console.log('getDetail error:', e);
    } finally {
      if (!silent) {
        setStudentSettingsLoading(false);
      }
    }
  };

  React.useEffect(() => {
    getDetail(false);
  }, []);

  const invitationStatusErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (typeof error?.message === 'string' && error.message) {
      return error.message;
    }
    return 'Failed to update invitation status.';
  };

  /** Invite email flow: backend resolves link via token; no separate update-* call. */
  const getPartnerEmailInviteTokenForApi = (item: any): string | null => {
    const raw = item?.partner_email_invite_token_hash ?? item?.partnerEmailInviteTokenHash;
    if (raw == null || String(raw).trim() === '') return null;
    return String(raw).trim();
  };

  const isParentRoleForPartnerInvite = () =>
    personalStore?.user_role === USER_ROLE.parent ||
    role === USER_ROLE.parent ||
    sessionStorage.getItem('userRole') === USER_ROLE.parent;

  /**
   * When invite_token is present, only make-token-used is called (returns true).
   * Otherwise returns false so caller runs updatePartnerInviteStatus.
   */
  const markPartnerEmailInviteTokenUsedIfPresent = async (
    item: any,
    status:
      | typeof INVITATION_STATUS.approved
      | typeof INVITATION_STATUS.declined
      | typeof INVITATION_STATUS.removed
  ): Promise<boolean> => {
    if (!isParentRoleForPartnerInvite()) return false;
    const invite_token = getPartnerEmailInviteTokenForApi(item);
    if (!invite_token) return false;
    await partnerServices.makeTokenUsed({ invite_token, status });
    return true;
  };

  const handleApproval = async (item: any) => {
    if (isDemoStudentEmail(item?.email)) {
      toastDemoAccountBlocked();
      return;
    }
    if (item && isParentInviteActionPending(item)) {
      const student_id = Number(item.student_id);
      const parent_email = personalStore?.email;
      const studentEmail = item?.email;
      const viaInviteToken = !!getPartnerEmailInviteTokenForApi(item);
      const canProceedWithoutToken =
        normalizeInviteBy(item) === 'student' ? !!studentEmail : !!parent_email;
      if (Number.isNaN(student_id) || !(viaInviteToken || canProceedWithoutToken)) {
        toast.error(
          normalizeInviteBy(item) === 'student'
            ? 'Student email is missing.'
            : 'Parent email is missing.'
        );
        return;
      }
      setWaiting(true);
      setPendingAction('approve');
      setCurrentRow(item);
      try {
        const usedInviteToken = await markPartnerEmailInviteTokenUsedIfPresent(
          item,
          INVITATION_STATUS.approved
        );
        if (!usedInviteToken) {
          await partnerServices.updatePartnerInviteStatus(
            item.student_id,
            parentId,
            INVITATION_STATUS.approved
          );
        }
        // await partnerServices.sync(item?.email);
        await getDetail(true);
        refreshChatList();
        toast.success('Invitation approved successfully.');
      } catch (error: any) {
        toast.error(invitationStatusErrorMessage(error));
      } finally {
        setWaiting(false);
        setPendingAction(null);
      }
    }
  };

  const handleDecline = async (item: any) => {
    if (isDemoStudentEmail(item?.email)) {
      toastDemoAccountBlocked();
      return;
    }
    if (item && isParentInviteActionPending(item)) {
      const student_id = Number(item.student_id);
      const parent_email = personalStore?.email;
      const studentEmail = item?.email;
      const viaInviteToken = !!getPartnerEmailInviteTokenForApi(item);
      const canProceedWithoutToken =
        normalizeInviteBy(item) === 'student' ? !!studentEmail : !!parent_email;
      if (Number.isNaN(student_id) || !(viaInviteToken || canProceedWithoutToken)) {
        toast.error(
          normalizeInviteBy(item) === 'student'
            ? 'Student email is missing.'
            : 'Parent email is missing.'
        );
        return;
      }
      setWaiting(true);
      setPendingAction('decline');
      setCurrentRow(item);
      try {
        const usedInviteToken = await markPartnerEmailInviteTokenUsedIfPresent(
          item,
          INVITATION_STATUS.declined
        );
        if (!usedInviteToken) {
          await partnerServices.updatePartnerInviteStatus(
            item.student_id,
            parentId,
            INVITATION_STATUS.declined
          );
        }
        // await partnerServices.sync(item?.email);
        await getDetail(true);
        refreshChatList();
        toast.success('Invitation declined successfully.');
      } catch (error: any) {
        toast.error(invitationStatusErrorMessage(error));
      } finally {
        setWaiting(false);
        setPendingAction(null);
      }
    }
  };

  const openRemoveStudentDialog = (row: any) => {
    if (isDemoStudentEmail(row?.email)) {
      toastDemoAccountBlocked();
      return;
    }
    setRemoveStudentRow(row);
    setRemoveStudentOpen(true);
  };

  const closeRemoveStudentDialog = () => {
    if (waiting) return;
    setRemoveStudentOpen(false);
    setRemoveStudentRow(null);
  };

  const confirmRemoveStudent = async () => {
    const item = removeStudentRow;
    if (!item || !isInviteListRowConnected(item)) {
      closeRemoveStudentDialog();
      return;
    }
    setWaiting(true);
    setCurrentRow(item);
    try {
      await partnerServices.updatePartnerInviteStatus(
        item.student_id,
        parentId,
        INVITATION_STATUS.removed
      );
      // await partnerServices.sync(item?.email);
      await getDetail(true);
      refreshChatList();
      toast.success('Student removed successfully.');
      setRemoveStudentOpen(false);
      setRemoveStudentRow(null);
    } catch (error: any) {
      toast.error(invitationStatusErrorMessage(error));
    } finally {
      setWaiting(false);
    }
  };

  const closeResendErrorDialog = () => {
    setResendErrorOpen(false);
    setResendErrorMessage('');
  };

  const handleCopyRowInviteLink = async (link: string, rowKey: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedRowKey(rowKey);
      setTimeout(
        () => setCopiedRowKey((prev) => (prev === rowKey ? null : prev)),
        2000
      );
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const handleResendPending = async (row: any) => {
    const inviteId = row?.id;
    if (inviteId == null || inviteId === '') {
      toast.error('Missing invitation.');
      return;
    }
    const idNum = Number(inviteId);
    if (Number.isNaN(idNum)) {
      toast.error('Invalid invitation.');
      return;
    }
    if (isDemoStudentEmail(row?.invitee_email ?? row?.email)) {
      toastDemoAccountBlocked();
      return;
    }
    setWaiting(true);
    try {
      await partnerServices.resendOutgoingPartnerEmailInvite(idNum);
      toast.success('Invitation sent.');
      await getDetail(true);
      refreshChatList();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        (typeof error?.response?.data === 'string' ? error.response.data : '') ??
        error?.message ??
        '';
      setResendErrorMessage(String(msg).trim() || 'Could not resend invitation.');
      setResendErrorOpen(true);
    } finally {
      setWaiting(false);
    }
  };

  const handleCancelOutboundInvite = async (row: any): Promise<boolean> => {
    if (isDemoStudentEmail(row?.invitee_email ?? row?.email)) {
      toastDemoAccountBlocked();
      return false;
    }
    const inviteId = row?.id;
    if (inviteId == null || inviteId === '') {
      toast.error('Missing invitation.');
      return false;
    }
    setWaiting(true);
    try {
      await partnerServices.cancelOutgoingPartnerEmailInvite(Number(inviteId));
      toast.success('Invitation cancelled.');
      await getDetail(true);
      refreshChatList();
      return true;
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? 'Could not cancel invitation.');
      return false;
    } finally {
      setWaiting(false);
    }
  };

  const openCancelInviteDialog = (row: any) => {
    setCancelInviteRow(row);
    setCancelInviteOpen(true);
  };

  const closeCancelInviteDialog = () => {
    setCancelInviteOpen(false);
    setCancelInviteRow(null);
  };

  const confirmCancelOutgoingInvite = async () => {
    if (!cancelInviteRow) return;
    const ok = await handleCancelOutboundInvite(cancelInviteRow);
    if (ok) {
      setCancelInviteOpen(false);
      setCancelInviteRow(null);
    }
  };

  const connectionRequestRows = React.useMemo(
    () =>
      partnerList
        .filter((r) => isParentInviteActionPending(r))
        .map((r) => ({
          ...r,
          partner_email_invite_token_hash:
            r.partner_email_invite_token_hash ?? r.partnerEmailInviteTokenHash ?? null,
        })),
    [partnerList]
  );

  const connectedRows = React.useMemo(
    () => partnerList.filter((r) => isInviteListRowConnected(r)),
    [partnerList]
  );

  /** Same graduation year sources as ParentMainDashboard (student_info + flat fields). */
  const classYearLabel = (row: any) => {
    const y =
      row?.student_info?.year_finish_high_school ??
      row?.year_finish_high_school ??
      row?.graduation_year ??
      row?.hs_graduation_year ??
      row?.class_of ??
      row?.expected_graduation_year;
    if (y == null || y === '') return null;
    return `Class of ${y}`;
  };

  const studentPlan = (row: any) => {
    const sid = row?.student_id;
    const key = sid != null && sid !== '' ? String(sid) : '';
    if (key && Object.prototype.hasOwnProperty.call(subscriptionPlanByStudentId, key)) {
      return subscriptionPlanByStudentId[key];
    }
    return (
      row?.subcription_plan ??
      row?.subscription_plan ??
      row?.stripe_plan ??
      get(row, 'payload.subcription_plan')
    );
  };

  const accessCopy = (plan: string | undefined) => {
    const p = planBadgeClass(plan);
    if (p === 'starter') {
      return 'Cannot view profile — upgrade to Silver or Gold to unlock.';
    }
    return 'Full profile access — read only.';
  };

  const inviteStudentEmailInList = isInviteStudentEmailAlreadyListed(inviteformik.values.student_email);

  const viewStudentProfile = (row: any) => {
    const sid = row?.student_id;
    if (!sid) return;
    const idNum = Number(sid);
    if (!Number.isNaN(idNum)) {
      dispatch(setCurrentStudentId(idNum as any));
    }
    history.push(routers.viewStudent.path.replace(':id', String(sid)));
  };

  const firstNameOnly = (full: string) => (full || '').trim().split(/\s+/)[0] || 'this student';

  return (
    <div className="tab-wrap-panel">
      <div className="">
        <div className="">
          <div className="text-left" style={{ paddingBottom: '16px' }}>
            <div
              className={`sp-parent-settings-wrap${studentSettingsLoading ? ' sp-parent-settings-wrap--loading' : ''}`}
            >
              {studentSettingsLoading && (
                <div
                  className="sp-parent-settings-loading"
                  aria-busy="true"
                  aria-live="polite"
                  aria-label="Loading students settings"
                >
                  <Spinner animation="border" role="status" variant="primary" />
                  <span className="sp-parent-settings-loading__text">
                    Loading students &amp; invitations…
                  </span>
                </div>
              )}
              <div
                className={`sp-parent-settings${studentSettingsLoading ? ' sp-parent-settings--loading' : ''}`}
              >
            <section className="sp-section" aria-labelledby="sp-your-students-heading">
              <div
                className="sp-section-head"
                style={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
              >
                <div className="d-flex align-items-start gap-2">
                  <div className="sp-icon-wrap" aria-hidden>
                    <GroupsOutlinedIcon sx={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h2 id="sp-your-students-heading" style={{ fontSize: '1.125rem', margin: 0 }}>
                      Your Students
                    </h2>
                    <p className="sp-section-sub" style={{ marginTop: 6, marginBottom: 0 }}>
                      Manage and track your students&apos; college application progress
                    </p>
                  </div>
                </div>
                <span
                  className="sp-count-badge sp-count-badge--blue"
                  style={{ alignSelf: 'center', padding: '6px 14px', height: 'auto', fontSize: '0.8125rem' }}
                >
                  {connectedRows.length} students
                </span>
              </div>
            </section>

              {/* Connected students — same card system as Invite Another Child / SettingsParent */}
              <section className="sp-section mb-1" aria-labelledby="ss-connected-heading">
                <h2 id="ss-connected-heading" className="sr-only">
                  Connected students
                </h2>
                {connectedRows.length === 0 ? (
                  <p className="sp-empty-hint mb-4">No connected students yet.</p>
                ) : (
                  connectedRows.map((row) => {
                    const name = catName(row.first_name, row.middle_name, row.last_name) || '—';
                    const initials =
                      `${(row.first_name?.[0] ?? '').toUpperCase()}${(row.last_name?.[0] ?? '').toUpperCase()}` ||
                      '?';
                    const rel = relationLabelOrParent(row.relationship ?? row.partner_relationship);
                    const cy = classYearLabel(row);
                    const plan = studentPlan(row);
                    const planName = getPlanDisplayName(plan);
                    const badgeClass = planBadgeClass(plan);
                    const starter = badgeClass === 'starter';
                    const isDemoRow = isDemoStudentEmail(row?.email);

                    return (
                      <div className="sp-card" key={partnerRowKey(row)}>
                        <div className="sp-person-row">
                          <div className="sp-avatar" aria-hidden>
                            {initials}
                          </div>
                          <div className="sp-person-main">
                            <p className="sp-person-name-line">
                              <span className="sp-person-name sp-person-name--inline">{name}</span>
                              {cy ? (
                                <>
                                  <span className="sp-person-name-dot"> · </span>
                                  <span className="sp-person-class-year">{cy}</span>
                                </>
                              ) : null}
                              <span className="sp-person-name-dot"> · </span>
                              <span className="sp-person-relation">{rel}</span>
                            </p>
                            <p className="sp-person-email mb-0">{row.email}</p>
                            <div className="ss-badge-row">
                              <span
                                className={`ss-plan-badge ss-plan-badge--${badgeClass}`}
                              >
                                {planName}
                              </span>
                              <span className="ss-badge-connected">
                                <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                                Connected
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="sp-info-box">
                          <p style={{ margin: 0 }}>{accessCopy(plan)}</p>
                        </div>
                        <div className="sp-actions-row">
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={() => viewStudentProfile(row)}
                            disabled={starter && !isDemoRow}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            View Profile
                          </Button>
                          {starter && (
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={() => history.push(routers.parentUpgrade.path)}
                              disabled={isDemoRow}
                            >
                              Upgrade {firstNameOnly(name)}
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="light"
                            size="sm"
                            className="sp-btn-outline"
                            onClick={() => history.push(routers.parentUpgrade.path)}
                            disabled={isDemoRow}
                          >
                            Manage Plan
                          </Button>
                          <button
                            type="button"
                            className="sp-link-danger"
                            onClick={() => openRemoveStudentDialog(row)}
                            disabled={
                              isDemoRow || (waiting && currentRow?.student_id === row.student_id)
                            }
                          >
                            {waiting && currentRow?.student_id === row.student_id ? (
                              <Spinner size="sm" animation="border" variant="dark" />
                            ) : (
                              'Remove'
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </section>

              {/* Invite another child — same structure as SettingsParent invite-parent; no Relationship column */}
              <section
                className="sp-section sp-section--invite"
                id="invite-student"
                aria-labelledby="sp-invite-child-heading"
              >
                <div className="sp-card sp-invite-card">
                  <h2 id="sp-invite-child-heading" className="sp-invite-card__title">
                    Invite Another Child
                  </h2>
                  <p className="sp-invite-card__desc">
                    Enter your child&apos;s email address. They will receive an invitation to connect their
                    GradMap account. Once connected, upgrade them to Silver or Gold to unlock full parent
                    access.
                  </p>
                  <div className="sp-form-grid sp-invite-form sp-invite-form--grid">
                    <div className="sp-invite-form__cell">
                      <TextIconInput
                        icon={<PersonOutlinedIcon />}
                        iconClass="text-primary3"
                        formClass="text-field-dark border-field-dark"
                        mdCol={'12'}
                        label={inviteStudentNamesLocked ? 'First Name' : 'First Name*'}
                        name={'student_first_name'}
                        placeholder="First name"
                        values={inviteformik.values.student_first_name}
                        disabled={
                          inviteStudentNamesLocked ||
                          inviteStudentEmailChecking ||
                          inviteStudentEmailInList
                        }
                        onChange={inviteformik.handleChange}
                        feedback={
                          inviteformik.touched.student_first_name &&
                          inviteformik.errors.student_first_name && (
                            <span className="text-danger">{inviteformik.errors.student_first_name}</span>
                          )
                        }
                      />
                    </div>
                    <div className="sp-invite-form__cell">
                      <TextIconInput
                        icon={<PersonOutlinedIcon />}
                        iconClass="text-primary3"
                        formClass="text-field-dark border-field-dark"
                        mdCol={'12'}
                        label={inviteStudentNamesLocked ? 'Last Name' : 'Last Name*'}
                        name={'student_last_name'}
                        placeholder="Last name"
                        values={inviteformik.values.student_last_name}
                        disabled={
                          inviteStudentNamesLocked ||
                          inviteStudentEmailChecking ||
                          inviteStudentEmailInList
                        }
                        onChange={inviteformik.handleChange}
                        feedback={
                          inviteformik.touched.student_last_name &&
                          inviteformik.errors.student_last_name && (
                            <span className="text-danger">{inviteformik.errors.student_last_name}</span>
                          )
                        }
                      />
                    </div>
                    <div className="sp-invite-form__cell sp-invite-form__cell--full">
                      <label className="sp-invite-field-label" htmlFor="invite-student-email">
                        Student Email*
                      </label>
                      <div className="sp-invite-email-row">
                        <div className="sp-invite-email-row__field sp-invite-form__cell">
                          <TextIconInput
                            hideLabel
                            id="invite-student-email"
                            icon={<MailOutlineIcon />}
                            iconClass="text-primary3"
                            formClass="text-field-dark border-field-dark"
                            mdCol={'12'}
                            label={'Student Email*'}
                            name={'student_email'}
                            placeholder="child@example.com"
                            values={inviteformik.values.student_email}
                            disabled={inviteStudentEmailChecking}
                            onBlur={handleInviteStudentEmailBlur}
                            onChange={(e) => {
                              inviteformik.handleChange(e);
                              if (inviteStudentNamesLocked) {
                                inviteformik.setFieldValue('student_first_name', '');
                                inviteformik.setFieldValue('student_last_name', '');
                              }
                              setInviteStudentNamesLocked(false);
                            }}
                            feedback={
                              inviteStudentEmailInList ? (
                                <span id="invite_student_already_listed" className="text-danger">
                                  This student is already in your invitation list.
                                </span>
                              ) : (
                                inviteformik.touched.student_email &&
                                inviteformik.errors.student_email && (
                                  <span id="error_email" className="text-danger">
                                    {inviteformik.errors.student_email}
                                  </span>
                                )
                              )
                            }
                          />
                          {inviteStudentEmailChecking && !inviteStudentEmailInList && (
                            <span className="text-secondary fs-13px d-block mt-1">Checking email…</span>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <Button
                            variant="primary"
                            className="sp-btn-invite-send btn-update"
                            onClick={() => inviteformik.handleSubmit()}
                            disabled={
                              inviteStudentEmailChecking ||
                              waiting ||
                              inviteStudentEmailInList ||
                              isDemoStudentEmail(inviteformik.values.student_email?.trim())
                            }
                          >
                            {inviteformik.isSubmitting ? (
                              <>
                                <Spinner
                                  size="sm"
                                  animation="border"
                                  variant="light"
                                  className="me-2 align-middle"
                                  role="status"
                                  aria-hidden
                                />
                                <span className="align-middle">Inviting…</span>
                              </>
                            ) : (
                              <>
                                <SendIcon sx={{ fontSize: 18 }} />
                                Send Invitation
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Pending outgoing email invites */}
              <section className="sp-section" aria-labelledby="ss-pending-out-heading">
                <div className="sp-section-head">
                  <h2 id="ss-pending-out-heading" style={{ margin: 0 }}>
                    Pending Invitations
                  </h2>
                  {outgoingEmailInvites.length > 0 && (
                    <span className="sp-count-badge sp-count-badge--amber">
                      {outgoingEmailInvites.length}
                    </span>
                  )}
                </div>
                {outgoingEmailInvites.length === 0 ? (
                  <p className="sp-empty-hint">No pending invitations.</p>
                ) : (
                  outgoingEmailInvites.map((row) => {
                    const sent =
                      row.created_at &&
                      `Sent ${new Date(row.created_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}`;
                    const isPendingDemo = isDemoStudentEmail(row.invitee_email ?? row.email);
                    return (
                      <div className="sp-card" key={outgoingInviteRowKey(row)}>
                        <div className="sp-person-row">
                          <div className="sp-person-main">
                            <p className="sp-person-email fw-bold mb-1">
                              {row.invitee_email ?? row.email}
                            </p>
                            {sent ? <span className="sp-meta-date d-block">{sent}</span> : null}
                          </div>
                        </div>
                        <div className="sp-actions-row">
                          <Button
                            type="button"
                            className="sp-btn-outline"
                            variant="light"
                            onClick={() => handleResendPending(row)}
                            disabled={waiting || isPendingDemo}
                          >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                            Resend
                          </Button>
                          {row.invite_link && (
                            <Button
                              type="button"
                              className="sp-btn-outline"
                              variant="light"
                              disabled={waiting || isPendingDemo || row.is_expired}
                              onClick={() =>
                                handleCopyRowInviteLink(
                                  row.invite_link,
                                  String(row.id ?? row.invitee_email ?? 'invite')
                                )
                              }
                            >
                              {copiedRowKey === String(row.id ?? row.invitee_email ?? 'invite') ? (
                                <>
                                  <CheckIcon sx={{ fontSize: 18 }} />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                                  Copy Invite Link
                                </>
                              )}
                            </Button>
                          )}
                          <button
                            type="button"
                            className="sp-link-danger"
                            onClick={() => openCancelInviteDialog(row)}
                            disabled={waiting || isPendingDemo}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </section>

              {/* Connection requests — compact card (name + class, Pending, Awaiting, Accept / Decline) */}
              <section className="sp-section" aria-labelledby="ss-requests-heading">
                <div className="sp-section-head">
                  <h2 id="ss-requests-heading" style={{ margin: 0 }}>
                    Connection Requests
                  </h2>
                  {connectionRequestRows.length > 0 && (
                    <span className="sp-count-badge sp-count-badge--blue">
                      {connectionRequestRows.length}
                    </span>
                  )}
                </div>
                {connectionRequestRows.length === 0 ? (
                  <p className="sp-empty-hint">No connection requests.</p>
                ) : (
                  connectionRequestRows.map((row) => {
                    const name = catName(row.first_name, row.middle_name, row.last_name) || '—';
                    const initials =
                      `${(row.first_name?.[0] ?? '').toUpperCase()}${(row.last_name?.[0] ?? '').toUpperCase()}` ||
                      '?';
                    const cy = classYearLabel(row);
                    const isReqDemo = isDemoStudentEmail(row?.email);

                    return (
                      <div className="sp-card ss-connection-request-card" key={partnerRowKey(row)}>
                        <div className="sp-person-row ss-connection-request-card__top">
                          <div className="sp-avatar" aria-hidden>
                            {initials}
                          </div>
                          <div className="sp-person-main ss-connection-request-card__main">
                            <div className="ss-connection-request-card__name-row">
                              <p className="sp-person-name-line ss-connection-request-card__name-line mb-0">
                                <span className="sp-person-name sp-person-name--inline">{name}</span>
                                {cy ? (
                                  <>
                                    <span className="sp-person-name-dot"> · </span>
                                    <span className="sp-person-class-year">{cy}</span>
                                  </>
                                ) : null}
                              </p>
                              <span className="sp-badge sp-badge--request-muted">Pending</span>
                            </div>
                            <div className="ss-connection-request-card__await">
                              <span className="sp-badge sp-badge--await">Awaiting Your Approval</span>
                            </div>
                          </div>
                        </div>
                        <div className="sp-actions-row ss-connection-request-card__actions">
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            className="ss-connection-request-card__btn-accept"
                            onClick={() => handleApproval(row)}
                            disabled={
                              isReqDemo ||
                              (waiting &&
                                currentRow?.student_id === row.student_id &&
                                pendingAction === 'decline')
                            }
                          >
                            {waiting &&
                            pendingAction === 'approve' &&
                            currentRow?.student_id === row.student_id ? (
                              <Spinner size="sm" animation="border" variant="light" />
                            ) : (
                              <>
                                <CheckIcon sx={{ fontSize: 18 }} />
                                Accept
                              </>
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="light"
                            size="sm"
                            className="sp-btn-decline ss-connection-request-card__btn-decline"
                            onClick={() => handleDecline(row)}
                            disabled={
                              isReqDemo ||
                              (waiting &&
                                currentRow?.student_id === row.student_id &&
                                pendingAction === 'approve')
                            }
                          >
                            {waiting &&
                            pendingAction === 'decline' &&
                            currentRow?.student_id === row.student_id ? (
                              <Spinner size="sm" animation="border" variant="dark" />
                            ) : (
                              'Decline'
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="go-to-bottom" />

      <Dialog
        open={removeStudentOpen}
        onClose={(_, reason) => {
          if (waiting) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            closeRemoveStudentDialog();
          }
        }}
        aria-labelledby="ss-remove-student-title"
        aria-describedby="ss-remove-student-desc"
      >
        <DialogTitle id="ss-remove-student-title">Remove this student?</DialogTitle>
        <DialogContent>
          <DialogContentText id="ss-remove-student-desc">
            {removeStudentRow?.email ? (
              <>
                This will disconnect{' '}
                <strong>
                  {catName(
                    removeStudentRow.first_name,
                    removeStudentRow.middle_name,
                    removeStudentRow.last_name
                  ) || 'this student'}
                </strong>{' '}
                ({removeStudentRow.email}) from your account. You can invite them again later.
              </>
            ) : (
              'This will disconnect this student from your account. You can invite them again later.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={closeRemoveStudentDialog} disabled={waiting}>
            Back
          </MuiButton>
          <MuiButton
            onClick={confirmRemoveStudent}
            color="error"
            variant="contained"
            disabled={waiting}
          >
            {waiting ? 'Removing…' : 'Remove'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelInviteOpen}
        onClose={(_, reason) => {
          if (waiting) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            closeCancelInviteDialog();
          }
        }}
        aria-labelledby="ss-cancel-invite-title"
      >
        <DialogTitle id="ss-cancel-invite-title">Cancel invitation?</DialogTitle>
        <DialogContent>
          <DialogContentText id="ss-cancel-invite-desc">
            {cancelInviteRow?.invitee_email ? (
              <>
                This will remove the pending invitation sent to{' '}
                <strong>{cancelInviteRow.invitee_email}</strong>.
              </>
            ) : (
              'This will remove the pending invitation.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={closeCancelInviteDialog} disabled={waiting}>
            Back
          </MuiButton>
          <MuiButton
            onClick={confirmCancelOutgoingInvite}
            color="error"
            variant="contained"
            disabled={waiting}
          >
            {waiting ? 'Cancelling…' : 'Cancel invitation'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resendErrorOpen}
        onClose={(_, reason) => {
          if (waiting) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            closeResendErrorDialog();
          }
        }}
        aria-labelledby="ss-resend-error-title"
      >
        <DialogTitle id="ss-resend-error-title">Could not resend</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{resendErrorMessage}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton onClick={closeResendErrorDialog} variant="contained">
            OK
          </MuiButton>
        </DialogActions>
      </Dialog>

      <style scoped>{`
#new-counselor .form-control:disabled, 
#new-counselor .form-control[readonly] {
  background-color: #fff;
  opacity: 1;
}
#new-counselor input.text-field-light.border-field-light.form-control:disabled, 
#new-counselor input.text-field-light.border-field-light.form-control[readonly] {
  border-color: #8894AA !important;
  opacity: 1!important;
}

.btn.btn-approval, .btn.btn-remove {
  width: 103px!important;
  height: 44px!important;
  border-radius: 10px !important;
}

.btn.btn-setdefault {
  width: 120px!important;
  height: 44px!important;
  border-radius: 10px !important;
}

.btn.btn-remove.btn-light { 
  color: #0C2551!important;
  border-color: #EFF1F5!important;
  background-color: #EFF1F5!important; 
}

.btn.btn-remove.btn-light:hover { 
  color: #fff!important;
  background-color: #DC3746!important;
  border-color: #DC3746!important;
}

    `}</style>
    </div>
  );
};

export default SettingsStudents;
